/* eslint-disable prettier/prettier */
/* eslint-disable react/jsx-indent */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, FieldArray } from 'formik';
import { Row, Col } from 'reactstrap';
import ModalPopup from 'components/organisms/modal';
import Button from 'components/atoms/button';
import Input from 'components/atoms/input';
import forms from 'constants/forms';
import dataService from 'services/dataService';
import { showResult, getInitialValues } from 'utils';
import * as Yup from 'yup';
import common from 'constants/common';
import useConfirm from 'utils/useConfirm';
import TrashIcon from 'assets/img/trash-can.svg';
import ModalTitle from './modalTitle';

const CareLocationsSchema = Yup.object().shape({
  locations: Yup.array()
    .of(
      Yup.object().shape({
        facilityName: Yup.string().required('required'),
        facilityAddress: Yup.string().nullable(),
        facilityNumber: Yup.string()
          .matches(/^[+]?[0-9]*$/, 'Phone number is not correct')
          .nullable(),
        city: Yup.string().when('zipCode', {
          is: (zipCode) => !!zipCode,
          then: (schema) => schema.required('City is required').nullable(),
          otherwise: (schema) => schema.nullable(),
        }),
        state: Yup.string().when(['`zipCode`', 'city'], {
          is: (zipCode, city) => !!zipCode || !!city,
          then: Yup.string().required('State is required').nullable(),
          otherwise: Yup.string().nullable(),
        }),
        zipCode: Yup.string().nullable(),
      }),
    )
    .min(1, 'Need at least one location'),
});

const CARE_LOCATIONS_FIELDS = forms.careLocationsFields;

let formikHelpers;
const EditDaycareModal = ({
  isOpen,
  toggle,
  patientGuid,
  loading,
  locations,
  submit,
  getData,
}) => {
  const handleSubmit = async (values) => {
    const postData = {
      careLocations: [],
    };
    values.locations.forEach(async (locationValues) => {
      postData.careLocations.push({
        guid: locationValues.guid,
        facilityName: locationValues.facilityName,
        facilityAddress: locationValues.facilityAddress,
        facilityNumber: locationValues.facilityNumber,
        city: locationValues.city,
        state: locationValues.state,
        zipCode: locationValues.zipCode,
      });
    });

    const { error, data } = await submit({
      patientGuid,
      careLocations: postData,
    });

    if (data.errorLocationGuids.length > 0) {
      const errorGuid = `Warning: ${data.errorLocationGuids.join(
        ' ,',
      )} are not added!`;
      showResult(errorGuid, common.submitSuccessTypes.changed, toggle);
    }
    showResult(error, common.submitSuccessTypes.changed, toggle);
  };

  const showRemoveConfirm = useConfirm({
    message: 'Are you sure you want to delete the care location?',
    title: 'Confirm',
  });

  const defaultValues = useMemo(() => {
    const values = { locations: [] };
    locations?.forEach((location) => {
      const initialLocation = getInitialValues(CARE_LOCATIONS_FIELDS, location);
      initialLocation.guid = location.guid;
      values.locations.push(initialLocation);
    });
    return values;
  }, [CARE_LOCATIONS_FIELDS, locations]);

  const addCareLocationField = () => {
    formikHelpers.push(
      getInitialValues(CARE_LOCATIONS_FIELDS, {
        facilityName: '',
        facilityAddress: null,
        facilityNumber: null,
        city: null,
        state: null,
        zipCode: null,
      }),
    );
  };

  const handleRemove = async (location, index) => {
    if (location.guid) {
      if (await showRemoveConfirm()) {
        const { data, error } = await dataService.deleteObject(
          'core/ccm/care-locations',
          {
            guid: location.guid,
            patientGuid,
          },
        );
        if (data === '') {
          formikHelpers.remove(index);
          showResult(null, common.submitSuccessTypes.deleted);
          await getData(patientGuid);
        } else {
          showResult(error, common.submitSuccessTypes.changed);
        }
      }
    } else {
      formikHelpers.remove(index);
    }
  };

  return (
    <ModalPopup
      isOpen={isOpen}
      toggle={toggle}
      title={<ModalTitle addHandler={addCareLocationField} />}
      className="popup care-locations"
    >
      <Formik
        initialValues={defaultValues}
        onSubmit={handleSubmit}
        validationSchema={CareLocationsSchema}
      >
        {({ values, errors }) => (
          <Form className="user--form">
            <FieldArray name="locations">
              {(helpers) => {
                formikHelpers = helpers;
                return (
                  values.locations.length > 0 &&
                  values.locations.map((_location, locationIndex) => (
                    <section
                      className="care-locations__form"
                      key={locationIndex}
                    >
                      <Row>
                        {CARE_LOCATIONS_FIELDS.map((field, index) => (
                          <Col key={index} xl="4" lg="4" md="6" sm="6" xs="12">
                            <Input
                              {...field}
                              id={`locations.${locationIndex}.${field.id}`}
                            />
                            {errors.locations &&
                              errors.locations[locationIndex] &&
                              errors.locations[locationIndex][field.id] && (
                                <div className="input-error">
                                  {errors.locations[locationIndex][field.id]}
                                </div>
                              )}
                          </Col>
                        ))}
                      </Row>
                      <span
                        className="care-locations__remove-button"
                        onClick={() => handleRemove(_location, locationIndex)}
                      >
                        <img src={TrashIcon} alt="trash" />
                      </span>
                      <div className="care-locations__devider" />
                    </section>
                  ))
                );
              }}
            </FieldArray>

            <div className="text-center">
              <Button type="submit" text="Save" disabled={loading} />
            </div>
          </Form>
        )}
      </Formik>
    </ModalPopup>
  );
};

EditDaycareModal.defaultProps = {
  isOpen: false,
  locations: {},
};

EditDaycareModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  patientGuid: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  submit: PropTypes.func.isRequired,
  locations: PropTypes.array,
  getData: PropTypes.func,
};

export default EditDaycareModal;
